import { type Ref, computed } from 'vue';

import useOfflinePartner from '@/composables/useOfflinePartner';
import { useFeedsStore, useFileUploadsStore, useSourcesStore } from '@/stores';
import type { Feed } from '@/types/feeds';
import type { Source } from '@/types/sources';

interface DataProviderOptions {
  isOfflinePartner: Ref<boolean>;
  offlinePartnerUuid?: Ref<string | undefined>;
}

export function usePopulationDataProvider(options: DataProviderOptions) {
  const { isOfflinePartner, offlinePartnerUuid } = options;

  // Store references
  const feedsStore = useFeedsStore();
  const sourcesStore = useSourcesStore();
  const fileUploadsStore = useFileUploadsStore();
  const {
    offlineFeeds,
    offlineSources,
    offlineCurrentUploadNames,
    refreshOfflineData,
  } = useOfflinePartner();

  const currentFeeds = computed(() =>
    isOfflinePartner.value ? offlineFeeds.value : feedsStore.feeds,
  );

  const currentSources = computed(() =>
    isOfflinePartner.value ? offlineSources.value : sourcesStore.sources,
  );

  const currentUploadNames = computed(() =>
    isOfflinePartner.value
      ? offlineCurrentUploadNames.value
      : fileUploadsStore.currentUploadNames,
  );

  const getFeedById = (feedId: number): Feed | undefined => {
    return currentFeeds.value.find((feed) => feed.id === feedId);
  };

  const getSourcesByFeedId = (feedId: number): Source[] => {
    return currentSources.value.filter((source) => source.feed_id === feedId);
  };

  const initializeData = async (): Promise<void> => {
    if (isOfflinePartner.value && offlinePartnerUuid?.value) {
      await refreshOfflineData(offlinePartnerUuid.value);
    }
  };

  return {
    currentFeeds,
    currentSources,
    currentUploadNames,
    getFeedById,
    getSourcesByFeedId,
    initializeData,
  };
}
